import { z } from 'zod';
import { eventTimestampSchema, visitorIdSchema } from './common.js';
/**
 * The phase of SDK execution that emitted the error.
 * - 'flush'              : event POST failure (network, non-2xx, response validation)
 * - 'flag'               : flag fetch failure (network, validation, cache corruption)
 * - 'identify'           : identify() call failure
 * - 'storage'            : localStorage / sessionStorage error
 * - 'init'               : constructor error (caught + rethrown by SDK so host sees it too)
 * - 'capture'            : auto-capture handler error (click, scroll, form)
 * - 'unhandled'          : window.error matched an SDK frame
 * - 'unhandled_rejection': window.unhandledrejection matched an SDK frame
 */
export const sdkErrorPhaseSchema = z.enum([
    'flush',
    'flag',
    'identify',
    'storage',
    'init',
    'capture',
    'unhandled',
    'unhandled_rejection',
]);
/**
 * A single SDK-emitted error event. Keep PII out:
 * - `message` is sanitized client-side (URLs stripped of query strings, capped at 500 chars).
 * - `stack` is sanitized similarly (capped at 2KB).
 * - `url` is the page URL with query string stripped client-side.
 */
export const sdkErrorEventSchema = z
    .object({
    /** Client-minted UUIDv4 — server uses this for idempotency if needed. */
    event_id: z.string().uuid(),
    /** Visitor ID if available. Null when reporter fires before visitor_id is established. */
    visitor_id: visitorIdSchema.nullable(),
    /** Session ID if available. */
    session_id: z.string().max(255).nullable().optional(),
    /** ISO timestamp client-minted. The schema's eventTimestampSchema enforces "within 24 hours". */
    timestamp: eventTimestampSchema,
    /** Page URL (query-string-stripped client-side). */
    url: z.string().url().max(2048).nullable().optional(),
    phase: sdkErrorPhaseSchema,
    /** Sanitized error message, max 500 chars. */
    message: z.string().min(1).max(500),
    /** Sanitized stack trace, max 2000 chars. Optional. */
    stack: z.string().max(2000).nullable().optional(),
    /** SDK build version (baked at build time). */
    sdk_version: z.string().min(1).max(50),
    /** Stable hash (phase + first stack frame) for dedup. Client-computed. */
    error_signature: z.string().min(1).max(64),
    /** For 'flush' phase: number of events queued at time of failure. */
    queued_events: z.number().int().min(0).max(100_000).nullable().optional(),
    /** Free-form supplementary context (e.g. http_status). Capped to 10 string keys. */
    context: z
        .record(z.string().min(1).max(50), z.string().max(200))
        .refine((obj) => Object.keys(obj).length <= 10, {
        error: 'context must have at most 10 keys',
    })
        .optional(),
})
    .meta({
    id: 'SdkErrorEvent',
    description: 'A single SDK-emitted error event for client-side observability',
});
/**
 * Request envelope for POST /api/errors.
 * Batched up to 10 errors per request to limit error-storm bandwidth.
 */
export const sdkErrorRequestSchema = z
    .object({
    errors: z.array(sdkErrorEventSchema).min(1).max(10),
})
    .meta({
    id: 'SdkErrorRequest',
    description: 'Batch of SDK error events',
});
//# sourceMappingURL=errors.js.map